import React from "react";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Section from "../components/marketing/Section";
import SectionHeading from "../components/marketing/SectionHeading";

/**
 * /docs — single "how it works" page, per design-system/landing.md §6.
 *
 * Routing copy updated 2026-08-27. It had described assignment as workload-only
 * ("a reviewer with room in their queue") — correct when written, but stale once
 * RoutingSpotlight shipped, leaving /docs contradicting both the landing page
 * and /pricing on the product's headline claim. Now describes what
 * routing-engine.mjs actually does: file_expertise * 0.5 + workload * 0.3, with
 * the expertise term gated to Pro (webhook-github/index.mjs passes
 * includeFileExpertise: effectiveTier === 'pro').
 */
const TOPICS = [
  {
    id: "install",
    title: "Install",
    body: (
      <>
        <p className="mb-4">
          Click "Start free trial" and sign in with GitHub — there's no
          password to pick and no form to fill in. From there you install the
          Anthurium app on GitHub's own screen, choosing the organization and
          the repositories you want it to see.
        </p>
        <p>
          GitHub sends you back to Anthurium automatically, your account and
          team are created from that installation, and your dashboard is
          ready. Nothing to configure by hand — no webhook URLs, no secrets to
          copy.
        </p>
      </>
    ),
  },
  {
    id: "scoring",
    title: "How scoring works",
    body: (
      <>
        <p className="mb-4">
          Every pull request is scored 0–10 the moment it's opened, from
          lines changed, files touched, how many different file types the
          diff spans, and whether it touches a critical path — things like
          auth, payments, or billing code.
        </p>
        <p className="mb-4">
          Alongside the score, Anthurium estimates how long the review will
          take, and tags the PR automatically based on which files changed
          (backend, frontend, database, and similar).
        </p>
        <p className="mb-4">
          It's then routed to a reviewer. On Pro, that means whoever has
          actually modified the files in the pull request most often — built
          from your repo's own commit history — balanced against how many
          reviews they already have open, so expertise never means one person
          gets everything. On Free, routing balances workload alone. Every
          assignment records the reason it was made, visible on the PR card.
        </p>
        <p className="mb-4">
          Anthurium then requests that person as a reviewer on GitHub, so they
          get GitHub's usual notification and the pull request shows up in
          their review-requested queue. Requesting a reviewer is the only thing
          Anthurium ever writes to GitHub — it can't push commits, change
          files, or merge.
        </p>
        <p>
          All of this happens before anyone opens the diff — the goal is to
          know what you're looking at before you start reading it. Anthurium
          reads only file paths and line counts to do it; the contents of your
          diff are never read or stored.
        </p>
      </>
    ),
  },
  {
    id: "inviting-teammates",
    title: "Inviting teammates",
    body: (
      <>
        <p className="mb-4">
          Only the person who installs Anthurium needs to touch GitHub's
          install screen. Everyone else joins with an invite link — find it
          on your dashboard, share it with your team, and they sign in with
          GitHub to join. No installation, no repository selection, nothing
          to authorize on GitHub's side.
        </p>
        <p>
          Invite links are long, random, and only visible to whoever created
          the team, who can also regenerate one at any time from the
          dashboard — the old link stops working the moment a new one is
          generated.
        </p>
      </>
    ),
  },
  {
    id: "managing-repos",
    title: "Managing repos",
    body: (
      <>
        <p>
          Which repositories Anthurium can see is controlled entirely from
          GitHub, not from the Anthurium dashboard — the same screen you
          used to install it. Add a repo, remove one, or uninstall
          Anthurium entirely from your organization's GitHub settings at any
          time, and it takes effect immediately.
        </p>
      </>
    ),
  },
];

export default function DocsPage() {
  return (
    <div className="overflow-x-hidden bg-rf-bg-primary">
      <MarketingNav />

      <Section className="!pb-8">
        <SectionHeading eyebrow="Docs" title="How Anthurium works" />
      </Section>

      {TOPICS.map((topic, i) => (
        <Section
          key={topic.id}
          id={topic.id}
          variant={i % 2 === 1 ? "elevated" : "default"}
          className="!py-12 md:!py-16"
        >
          <div className="mx-auto max-w-[68ch]">
            <h2 className="mb-4 text-2xl font-bold tracking-tight text-rf-text-primary">
              {topic.title}
            </h2>
            <div className="text-sm leading-relaxed text-rf-text-secondary">{topic.body}</div>
          </div>
        </Section>
      ))}

      <MarketingFooter />
    </div>
  );
}
